import React, { useState, useEffect } from 'react';
import { useAuth, useLanguage, useTheme } from '../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import axios from 'axios';
import {
  User, Mail, Phone, Globe, Lock, Bell, Shield, Download,
  Camera, Save, LogOut, MapPin, GraduationCap, BookOpen, Award,
  Building2, Calendar, Hash, FileDown
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ProfilePage = () => {
  const { user, logout, updateUser } = useAuth();
  const { t, language, changeLanguage } = useLanguage();
  const { theme, updateTheme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [fullProfile, setFullProfile] = useState(null);
  const [formData, setFormData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    address: '',
    city: '',
    province: '',
    birth_date: '',
    birth_place: '',
    gender: '',
    nationality: 'Congolaise',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    blood_type: '',
    bio: '',
  });

  const isStudent = user?.role === 'student';

  useEffect(() => {
    if (isStudent) {
      loadFullProfile();
    }
  }, []);

  const loadFullProfile = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/students/me/full-profile`);
      setFullProfile(res.data);
      const student = res.data.student;
      if (student) {
        setFormData(prev => ({
          ...prev,
          address: student.address || '',
          city: student.city || '',
          province: student.province || '',
          birth_date: student.birth_date || '',
          birth_place: student.birth_place || '',
          gender: student.gender || '',
          nationality: student.nationality || 'Congolaise',
          emergency_contact_name: student.emergency_contact_name || '',
          emergency_contact_phone: student.emergency_contact_phone || '',
          blood_type: student.blood_type || '',
          bio: student.bio || '',
        }));
      }
    } catch (err) {
      console.error('Profile load error:', err);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.put(`${API_URL}/api/students/me/profile`, formData);
      updateUser({ first_name: formData.first_name, last_name: formData.last_name, phone: formData.phone });
      toast.success(language === 'fr' ? 'Profil mis a jour' : 'Profile updated');
      if (isStudent) loadFullProfile();
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      if (format === 'csv') {
        const res = await axios.get(`${API_URL}/api/students/me/export?format=csv`, { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'mes_donnees_josmart.csv');
        document.body.appendChild(link);
        link.click();
        link.remove();
      } else {
        const res = await axios.get(`${API_URL}/api/students/me/export?format=json`);
        const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'mes_donnees_josmart.json');
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      toast.success(language === 'fr' ? 'Donnees exportees' : 'Data exported');
    } catch (err) {
      toast.error(t('error'));
    } finally {
      setExporting(false);
    }
  };

  const userInitials = `${user?.first_name?.[0] || ''}${user?.last_name?.[0] || ''}`.toUpperCase();
  const fp = fullProfile;

  const l = language === 'fr' ? {
    personalInfo: 'Informations personnelles',
    updateInfo: 'Mettez a jour vos informations',
    academic: 'Academique',
    security: 'Securite',
    data: 'Mes donnees',
    address: 'Adresse physique',
    city: 'Ville',
    province: 'Province',
    matricule: 'Matricule',
    level: 'Niveau',
    program: 'Filiere',
    faculty: 'Faculte',
    department: 'Departement',
    university: 'Universite',
    academicYear: 'Annee academique',
    enrollmentDate: "Date d'inscription",
    creditsEarned: 'Credits obtenus',
    average: 'Moyenne generale',
    totalCourses: 'Nombre de cours',
    memberSince: 'Membre depuis',
    exportCSV: 'Exporter en CSV',
    exportJSON: 'Exporter en JSON',
    exportDesc: 'Telechargez une copie de vos donnees',
    password: 'Mot de passe',
    currentPwd: 'Mot de passe actuel',
    newPwd: 'Nouveau mot de passe',
    confirmPwd: 'Confirmer',
    changePwd: 'Changer le mot de passe',
    activeSessions: 'Sessions actives',
    currentSession: 'Session actuelle',
    logoutAll: 'Se deconnecter de tous les appareils',
    courses: 'Mes cours',
    notAssigned: 'Non assigne',
  } : {
    personalInfo: 'Personal Information',
    updateInfo: 'Update your profile information',
    academic: 'Academic',
    security: 'Security',
    data: 'My Data',
    address: 'Physical Address',
    city: 'City',
    province: 'Province',
    matricule: 'Student ID',
    level: 'Level',
    program: 'Program',
    faculty: 'Faculty',
    department: 'Department',
    university: 'University',
    academicYear: 'Academic Year',
    enrollmentDate: 'Enrollment Date',
    creditsEarned: 'Credits Earned',
    average: 'Overall Average',
    totalCourses: 'Total Courses',
    memberSince: 'Member since',
    exportCSV: 'Export as CSV',
    exportJSON: 'Export as JSON',
    exportDesc: 'Download a copy of your data',
    password: 'Password',
    currentPwd: 'Current password',
    newPwd: 'New password',
    confirmPwd: 'Confirm',
    changePwd: 'Change password',
    activeSessions: 'Active Sessions',
    currentSession: 'Current session',
    logoutAll: 'Log out from all devices',
    courses: 'My Courses',
    notAssigned: 'Not assigned',
  };

  return (
    <div className="space-y-6 animate-slide-in" data-testid="profile-page">
      <div>
        <h1 className="heading-2">{t('profile')}</h1>
        <p className="text-gray-500 mt-1">
          {language === 'fr' ? 'Gerez vos informations personnelles et preferences' : 'Manage your personal information and preferences'}
        </p>
      </div>

      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList className="flex-wrap">
          <TabsTrigger value="profile" className="gap-2"><User className="w-4 h-4" />{t('profile')}</TabsTrigger>
          {isStudent && <TabsTrigger value="academic" className="gap-2"><GraduationCap className="w-4 h-4" />{l.academic}</TabsTrigger>}
          <TabsTrigger value="settings" className="gap-2"><Bell className="w-4 h-4" />{t('settings')}</TabsTrigger>
          <TabsTrigger value="security" className="gap-2"><Shield className="w-4 h-4" />{l.security}</TabsTrigger>
        </TabsList>

        {/* ============ PROFILE TAB ============ */}
        <TabsContent value="profile">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Profile Card */}
            <Card className="lg:col-span-1">
              <CardContent className="p-6 text-center">
                <div className="relative inline-block mb-4">
                  <Avatar className="w-24 h-24">
                    <AvatarFallback className="bg-[var(--tenant-primary)] text-white text-2xl">{userInitials}</AvatarFallback>
                  </Avatar>
                  <Button size="icon" variant="secondary" className="absolute bottom-0 right-0 rounded-full w-8 h-8">
                    <Camera className="w-4 h-4" />
                  </Button>
                </div>
                <h3 className="font-semibold text-lg">{user?.first_name} {user?.last_name}</h3>
                <p className="text-gray-500 text-sm">{user?.email}</p>
                <Badge className="mt-2">{t(user?.role || 'student')}</Badge>

                {isStudent && fp?.student && (
                  <div className="mt-4 pt-4 border-t space-y-2 text-left">
                    <InfoRow icon={Hash} label={l.matricule} value={fp.student.matricule} />
                    <InfoRow icon={GraduationCap} label={l.level} value={fp.academic?.promotion?.academic_year || l.notAssigned} />
                    <InfoRow icon={BookOpen} label={l.program} value={fp.academic?.program?.name || l.notAssigned} />
                    <InfoRow icon={Building2} label={l.faculty} value={fp.academic?.faculty?.name || l.notAssigned} />
                    <InfoRow icon={Building2} label={l.department} value={fp.academic?.department?.name || l.notAssigned} />
                    <InfoRow icon={Building2} label={l.university} value={fp.academic?.university?.name || l.notAssigned} />
                  </div>
                )}

                <div className="mt-4 pt-4 border-t space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">{l.memberSince}</span>
                    <span>{user?.created_at ? new Date(user.created_at).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { month: 'short', year: 'numeric' }) : 'N/A'}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">{t('status')}</span>
                    <Badge variant="outline" className="badge-success">{t('active')}</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Edit Form */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>{l.personalInfo}</CardTitle>
                <CardDescription>{l.updateInfo}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{t('firstName')}</Label>
                    <Input value={formData.first_name} onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))} data-testid="profile-firstname" />
                  </div>
                  <div>
                    <Label>{t('lastName')}</Label>
                    <Input value={formData.last_name} onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))} data-testid="profile-lastname" />
                  </div>
                </div>
                <div>
                  <Label>{t('email')}</Label>
                  <Input type="email" value={formData.email} disabled />
                </div>
                <div>
                  <Label>{t('phone')}</Label>
                  <Input value={formData.phone} onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))} placeholder="+243 xxx xxx xxx" data-testid="profile-phone" />
                </div>

                {isStudent && (
                  <>
                    <Separator />
                    <p className="text-sm font-medium text-gray-700 flex items-center gap-2"><MapPin className="w-4 h-4" />{l.address}</p>
                    <div>
                      <Label>{l.address}</Label>
                      <Input value={formData.address} onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))} placeholder={language === 'fr' ? 'Av. de la Paix, N.12, Q. Makala' : 'Street address'} data-testid="profile-address" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>{l.city}</Label>
                        <Input value={formData.city} onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))} placeholder="Kinshasa" data-testid="profile-city" />
                      </div>
                      <div>
                        <Label>{l.province}</Label>
                        <Input value={formData.province} onChange={(e) => setFormData(prev => ({ ...prev, province: e.target.value }))} placeholder="Kinshasa" data-testid="profile-province" />
                      </div>
                    </div>

                    <Separator />
                    <p className="text-sm font-medium text-gray-700 flex items-center gap-2"><User className="w-4 h-4" />{language === 'fr' ? 'Informations personnelles' : 'Personal details'}</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>{language === 'fr' ? 'Date de naissance' : 'Date of birth'}</Label>
                        <Input type="date" value={formData.birth_date} onChange={(e) => setFormData(prev => ({ ...prev, birth_date: e.target.value }))} data-testid="profile-birthdate" />
                      </div>
                      <div>
                        <Label>{language === 'fr' ? 'Lieu de naissance' : 'Place of birth'}</Label>
                        <Input value={formData.birth_place} onChange={(e) => setFormData(prev => ({ ...prev, birth_place: e.target.value }))} placeholder="Kinshasa" data-testid="profile-birthplace" />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label>{language === 'fr' ? 'Genre' : 'Gender'}</Label>
                        <select className="flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm" value={formData.gender} onChange={(e) => setFormData(prev => ({ ...prev, gender: e.target.value }))} data-testid="profile-gender">
                          <option value="">{language === 'fr' ? 'Choisir' : 'Select'}</option>
                          <option value="M">{language === 'fr' ? 'Masculin' : 'Male'}</option>
                          <option value="F">{language === 'fr' ? 'Feminin' : 'Female'}</option>
                        </select>
                      </div>
                      <div>
                        <Label>{language === 'fr' ? 'Nationalite' : 'Nationality'}</Label>
                        <Input value={formData.nationality} onChange={(e) => setFormData(prev => ({ ...prev, nationality: e.target.value }))} data-testid="profile-nationality" />
                      </div>
                      <div>
                        <Label>{language === 'fr' ? 'Groupe sanguin' : 'Blood type'}</Label>
                        <select className="flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm" value={formData.blood_type} onChange={(e) => setFormData(prev => ({ ...prev, blood_type: e.target.value }))} data-testid="profile-bloodtype">
                          <option value="">-</option>
                          {['A+','A-','B+','B-','AB+','AB-','O+','O-'].map(bt => <option key={bt} value={bt}>{bt}</option>)}
                        </select>
                      </div>
                    </div>

                    <Separator />
                    <p className="text-sm font-medium text-gray-700 flex items-center gap-2"><Phone className="w-4 h-4" />{language === 'fr' ? "Contact d'urgence" : 'Emergency contact'}</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>{language === 'fr' ? 'Nom du contact' : 'Contact name'}</Label>
                        <Input value={formData.emergency_contact_name} onChange={(e) => setFormData(prev => ({ ...prev, emergency_contact_name: e.target.value }))} placeholder={language === 'fr' ? 'Nom du parent/tuteur' : 'Parent/guardian name'} data-testid="profile-emergency-name" />
                      </div>
                      <div>
                        <Label>{language === 'fr' ? 'Telephone' : 'Phone'}</Label>
                        <Input value={formData.emergency_contact_phone} onChange={(e) => setFormData(prev => ({ ...prev, emergency_contact_phone: e.target.value }))} placeholder="+243..." data-testid="profile-emergency-phone" />
                      </div>
                    </div>

                    <div>
                      <Label>Bio</Label>
                      <textarea className="flex w-full rounded-md border bg-transparent px-3 py-2 text-sm min-h-[60px]" value={formData.bio} onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))} placeholder={language === 'fr' ? 'Parlez de vous...' : 'Tell us about yourself...'} data-testid="profile-bio" />
                    </div>
                  </>
                )}

                <div className="flex justify-end">
                  <Button onClick={handleSave} disabled={loading} className="gap-2" data-testid="save-profile-button">
                    <Save className="w-4 h-4" />
                    {loading ? t('loading') : t('save')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ============ ACADEMIC TAB (Students only) ============ */}
        {isStudent && (
          <TabsContent value="academic">
            <div className="space-y-6">
              {/* Academic Stats */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-5 text-center">
                    <Award className="w-8 h-8 mx-auto mb-2 text-[var(--tenant-secondary)]" />
                    <p className="text-2xl font-bold text-gray-900">{fp?.academic?.average || fp?.grades_summary?.average || 0}/20</p>
                    <p className="text-sm text-gray-500">{l.average}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-5 text-center">
                    <GraduationCap className="w-8 h-8 mx-auto mb-2 text-[var(--tenant-primary)]" />
                    <p className="text-2xl font-bold text-gray-900">{fp?.academic?.total_credits || fp?.grades_summary?.credits_earned || 0}</p>
                    <p className="text-sm text-gray-500">{l.creditsEarned}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-5 text-center">
                    <BookOpen className="w-8 h-8 mx-auto mb-2 text-green-600" />
                    <p className="text-2xl font-bold text-gray-900">{fp?.courses?.length || 0}</p>
                    <p className="text-sm text-gray-500">{l.totalCourses}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Academic Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2"><GraduationCap className="w-5 h-5" />{l.academic}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <InfoField label={l.university} value={fp?.academic?.university?.name} />
                    <InfoField label={l.faculty} value={fp?.academic?.faculty?.name} />
                    <InfoField label={l.department} value={fp?.academic?.department?.name} />
                    <InfoField label={l.program} value={fp?.academic?.program?.name} />
                    <InfoField label={l.level} value={fp?.academic?.promotion?.year_label} />
                    <InfoField label={l.academicYear} value={fp?.academic?.promotion?.academic_year} />
                    <InfoField label={l.matricule} value={fp?.student?.matricule} />
                    <InfoField label={l.enrollmentDate} value={fp?.student?.enrollment_date ? new Date(fp.student.enrollment_date).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US') : null} />
                  </div>
                </CardContent>
              </Card>

              {/* Courses List */}
              {fp?.courses && fp.courses.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2"><BookOpen className="w-5 h-5" />{l.courses}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="courses-table">
                        <thead>
                          <tr className="border-b text-left text-gray-500">
                            <th className="pb-2 pr-4">{t('code')}</th>
                            <th className="pb-2 pr-4">{t('courseName')}</th>
                            <th className="pb-2 pr-4 text-center">{t('credits')}</th>
                            <th className="pb-2 pr-4">{t('teacher')}</th>
                            <th className="pb-2 text-center">{t('grade')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {fp.courses.map(c => (
                            <tr key={c.id} className="border-b last:border-0 hover:bg-gray-50" data-testid={`course-row-${c.code}`}>
                              <td className="py-2.5 pr-4 font-mono text-xs"><Badge variant="secondary">{c.code}</Badge></td>
                              <td className="py-2.5 pr-4 font-medium">{c.name}</td>
                              <td className="py-2.5 pr-4 text-center">{c.credits}</td>
                              <td className="py-2.5 pr-4 text-gray-600">{c.teacher_name}</td>
                              <td className="py-2.5 text-center">
                                {c.grade ? (
                                  <Badge className={c.grade.score / c.grade.max_score >= 0.5 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                                    {c.grade.score}/{c.grade.max_score}
                                  </Badge>
                                ) : (
                                  <span className="text-gray-400">--</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        )}

        {/* ============ SETTINGS TAB ============ */}
        <TabsContent value="settings">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Globe className="w-5 h-5" />{language === 'fr' ? 'Langue' : 'Language'}</CardTitle>
              </CardHeader>
              <CardContent>
                <Select value={language} onValueChange={changeLanguage}>
                  <SelectTrigger data-testid="language-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fr">Francais</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Bell className="w-5 h-5" />{t('notifications')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{language === 'fr' ? 'Notifications push' : 'Push notifications'}</p>
                    <p className="text-sm text-gray-500">{language === 'fr' ? 'Recevoir des alertes' : 'Receive alerts'}</p>
                  </div>
                  <Switch defaultChecked />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Emails</p>
                    <p className="text-sm text-gray-500">{language === 'fr' ? 'Recevoir par email' : 'Receive by email'}</p>
                  </div>
                  <Switch defaultChecked />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Download className="w-5 h-5" />{l.data}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-500 mb-4">{l.exportDesc}</p>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => handleExport('csv')} disabled={exporting} data-testid="export-csv-button">
                    <FileDown className="w-4 h-4 mr-2" />{l.exportCSV}
                  </Button>
                  <Button variant="outline" onClick={() => handleExport('json')} disabled={exporting} data-testid="export-json-button">
                    <FileDown className="w-4 h-4 mr-2" />{l.exportJSON}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ============ SECURITY TAB ============ */}
        <TabsContent value="security">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Lock className="w-5 h-5" />{l.password}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div><Label>{l.currentPwd}</Label><Input type="password" /></div>
                <div><Label>{l.newPwd}</Label><Input type="password" /></div>
                <div><Label>{l.confirmPwd}</Label><Input type="password" /></div>
                <Button>{l.changePwd}</Button>
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle>{l.activeSessions}</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium">{l.currentSession}</p>
                    <p className="text-xs text-gray-500">Chrome - Kinshasa, RDC</p>
                  </div>
                  <Badge variant="outline" className="badge-success">{t('active')}</Badge>
                </div>
                <Button variant="destructive" onClick={logout} className="w-full gap-2" data-testid="logout-all-button">
                  <LogOut className="w-4 h-4" />{l.logoutAll}
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-2 text-sm">
    <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />
    <span className="text-gray-500">{label}:</span>
    <span className="font-medium text-gray-900 truncate">{value || '--'}</span>
  </div>
);

const InfoField = ({ label, value }) => (
  <div>
    <Label className="text-gray-500 text-xs">{label}</Label>
    <p className="font-medium text-gray-900 mt-0.5">{value || '--'}</p>
  </div>
);

export default ProfilePage;
